import React from "react";
import { X } from "lucide-react";

/**
 * Results modal shown after an allowances/deductions bulk import finishes.
 * Lists row-level errors returned by the backend and duplicate employee/date
 * combinations detected in the uploaded file itself (the import endpoint has
 * no upsert/skip-on-duplicate behavior, so two rows for the same employee on
 * the same date both get imported — this surfaces that instead of leaving it
 * silent). Styled to match the Employees import results modal
 * (pages/(admin)employeemanagement/components/modals/ImportResultsModal.jsx).
 */
const AllowanceDeductionImportResultsModal = ({
  data,
  onClose,
  t,
  theme = "light",
  language = "en",
  fonts = {},
}) => {
  if (!data) return null;

  const fontTiny = fonts.tiny || "11px";
  const fontSmall = fonts.small || "13px";
  const fontBody = fonts.body || "14px";

  const total = data.total ?? 0;
  const processed = data.processed ?? 0;
  const failed = data.failed ?? 0;
  const errors = Array.isArray(data.errors) ? data.errors : [];
  const duplicates = Array.isArray(data.duplicates) ? data.duplicates : [];
  const allowancesCreated = data.createdAllowances ?? 0;
  const deductionsCreated = data.createdDeductions ?? 0;

  const hasIssues = failed > 0 || duplicates.length > 0;
  const isRTL = language === "ar";

  const pill = (label, value, color) => (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: "4px",
        padding: "14px 20px",
        borderRadius: "14px",
        minWidth: "90px",
        background: theme === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)",
        border: `1.5px solid ${color}30`,
      }}
    >
      <span style={{ fontSize: "1.75rem", fontWeight: 800, color, lineHeight: 1 }}>{value}</span>
      <span
        style={{
          fontSize: fontTiny,
          fontWeight: 600,
          color: theme === "dark" ? "#94a3b8" : "#64748b",
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          textAlign: "center",
        }}
      >
        {label}
      </span>
    </div>
  );

  const sectionHead = (icon, label, color) => (
    <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "10px", marginTop: "4px" }}>
      <span style={{ fontSize: "1.1rem" }}>{icon}</span>
      <span style={{ fontSize: fontBody, fontWeight: 700, color }}>{label}</span>
    </div>
  );

  const renderTHead = (...cols) => (
    <thead>
      <tr>
        {cols.map((c, i) => (
          <th
            key={i}
            style={{
              fontSize: fontTiny,
              padding: "10px 12px",
              background: theme === "dark" ? "#1e293b" : "#f1f5f9",
              color: theme === "dark" ? "#94a3b8" : "#64748b",
              fontWeight: 700,
              textAlign: "start",
              borderBottom: `1px solid ${theme === "dark" ? "rgba(148,163,184,0.15)" : "rgba(15,23,42,0.08)"}`,
              textTransform: "uppercase",
              letterSpacing: "0.03em",
            }}
          >
            {c}
          </th>
        ))}
      </tr>
    </thead>
  );

  const tdStyle = {
    padding: "10px 12px",
    fontSize: fontSmall,
    color: theme === "dark" ? "#e2e8f0" : "#1e293b",
    borderBottom: `1px solid ${theme === "dark" ? "rgba(148,163,184,0.08)" : "rgba(15,23,42,0.05)"}`,
    textAlign: "start",
  };

  const tableStyle = {
    width: "100%",
    borderCollapse: "collapse",
    background: theme === "dark" ? "#0f172a" : "#ffffff",
  };

  return (
    <>
      {/* Backdrop */}
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 100001,
          background: "rgba(0, 0, 0, 0.6)",
          backdropFilter: "blur(4px)",
          WebkitBackdropFilter: "blur(4px)",
          animation: "adResultsFadeIn 300ms ease-out",
        }}
        onClick={onClose}
      />

      {/* Modal Content */}
      <div
        className="payroll-modal"
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          zIndex: 100002,
          animation: "adResultsSlideUp 400ms cubic-bezier(0.34, 1.56, 0.64, 1)",
          width: "min(860px, calc(100vw - 32px))",
          maxHeight: "88vh",
          overflowY: "auto",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          display: "flex",
          flexDirection: "column",
          gap: "20px",
          margin: 0,
          background:
            theme === "dark"
              ? "linear-gradient(135deg, #0f172a 0%, #1a2a3a 100%)"
              : "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
          borderRadius: 16,
          padding: "32px",
          boxShadow:
            theme === "dark"
              ? "0 25px 60px rgba(0,0,0,0.8), 0 0 1px rgba(255,255,255,0.1)"
              : "0 20px 50px rgba(0,0,0,0.12), 0 0 1px rgba(0,0,0,0.04)",
          border: theme === "dark" ? "1px solid rgba(255,255,255,0.1)" : "1px solid rgba(148,163,184,0.15)",
          direction: isRTL ? "rtl" : "ltr",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "8px" }}>
          <div>
            <h2
              style={{
                margin: 0,
                marginBottom: "8px",
                fontSize: "24px",
                fontWeight: 800,
                background: hasIssues
                  ? "linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)"
                  : "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                letterSpacing: "-0.02em",
              }}
            >
              {hasIssues ? "⚠️ " : "✅ "}
              {t ? t("adImportResultsTitle") : "Import Complete"}
            </h2>
            <p
              style={{
                margin: 0,
                fontSize: fontSmall,
                color: theme === "dark" ? "#94a3b8" : "#64748b",
                fontWeight: 500,
              }}
            >
              {t ? t("adImportResultsSubtitle") : "The file has been processed. Review the details below."}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t ? t("close") : "Close"}
            style={{
              position: "absolute",
              top: 20,
              insetInlineEnd: 20,
              background: theme === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.05)",
              border: "none",
              borderRadius: "10px",
              width: 36,
              height: 36,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              color: theme === "dark" ? "#94a3b8" : "#64748b",
              fontSize: "20px",
              fontWeight: 700,
              transition: "all 0.2s",
            }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Summary pills */}
        <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
          {pill(t ? t("adImportTotal") : "Total", total, "#2e7cba")}
          {pill(t ? t("adImportProcessed") : "Processed", processed, "#10b981")}
          {pill(t ? t("adImportAllowancesCreated") : "Allowances", allowancesCreated, "#0ea5e9")}
          {pill(t ? t("adImportDeductionsCreated") : "Deductions", deductionsCreated, "#8b5cf6")}
          {pill(t ? t("adImportFailed") : "Failed", failed, "#ef4444")}
          {pill(t ? t("adImportDuplicates") : "Duplicates", duplicates.length, "#f59e0b")}
        </div>

        {/* No issues banner */}
        {!hasIssues && (
          <div
            style={{
              padding: "20px",
              borderRadius: "14px",
              background: theme === "dark" ? "rgba(16,185,129,0.08)" : "rgba(16,185,129,0.06)",
              border: "1.5px solid rgba(16,185,129,0.3)",
              color: "#10b981",
              fontWeight: 600,
              fontSize: fontBody,
              textAlign: "center",
            }}
          >
            🎉 {t ? t("adImportNoIssues") : "All rows imported successfully with no issues!"}
          </div>
        )}

        {/* Errors */}
        {errors.length > 0 && (
          <div>
            {sectionHead("❌", `${t ? t("adImportErrorsSection") : "Failed Rows"} (${errors.length})`, "#ef4444")}
            <div
              style={{
                borderRadius: "12px",
                overflow: "hidden",
                border: theme === "dark" ? "1px solid rgba(239,68,68,0.25)" : "1px solid rgba(239,68,68,0.2)",
                maxHeight: "260px",
                overflowY: "auto",
                scrollbarWidth: "none",
              }}
            >
              <table style={{ ...tableStyle, minWidth: "100%" }}>
                {renderTHead(
                  t ? t("row") : "Row",
                  t ? t("adImportEmployeeCode") : "Employee Code",
                  t ? t("adImportReason") : "Reason",
                )}
                <tbody>
                  {errors.map((err, i) => (
                    <tr key={i} style={{ background: theme === "dark" ? "rgba(239,68,68,0.04)" : "rgba(239,68,68,0.02)" }}>
                      <td style={{ ...tdStyle, fontWeight: 700, color: "#ef4444", width: "70px" }}>
                        {err.row ?? err.index ?? "—"}
                      </td>
                      <td style={{ ...tdStyle, color: theme === "dark" ? "#cbd5e1" : "#334155" }}>
                        {err.clientCode || err.infinityCode || "—"}
                      </td>
                      <td style={{ ...tdStyle, color: theme === "dark" ? "#fca5a5" : "#b91c1c" }}>
                        {err.error || err.message || (t ? t("unknownError") : "Unknown error")}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Duplicates */}
        {duplicates.length > 0 && (
          <div>
            {sectionHead(
              "⚠️",
              `${t ? t("adImportDuplicatesSection") : "Duplicate Rows Detected"} (${duplicates.length})`,
              "#f59e0b",
            )}
            <p style={{ margin: "0 0 8px", fontSize: fontTiny, color: theme === "dark" ? "#94a3b8" : "#64748b" }}>
              {t
                ? t("adImportDuplicatesHint")
                : "These employee/date combinations appear more than once in the uploaded file — every matching row was still imported, so double-check these aren't accidental repeats."}
            </p>
            <div
              style={{
                borderRadius: "12px",
                overflow: "hidden",
                border: theme === "dark" ? "1px solid rgba(245,158,11,0.25)" : "1px solid rgba(245,158,11,0.2)",
                maxHeight: "220px",
                overflowY: "auto",
                scrollbarWidth: "none",
              }}
            >
              <table style={{ ...tableStyle, minWidth: "100%" }}>
                {renderTHead(
                  t ? t("adImportEmployeeCode") : "Employee Code",
                  t ? t("adImportDate") : "Date",
                  t ? t("adImportOccurrences") : "Occurrences",
                )}
                <tbody>
                  {duplicates.map((dup, i) => (
                    <tr key={i} style={{ background: theme === "dark" ? "rgba(245,158,11,0.04)" : "rgba(245,158,11,0.02)" }}>
                      <td style={{ ...tdStyle, fontWeight: 700, color: "#f59e0b" }}>{dup.employeeCode}</td>
                      <td style={{ ...tdStyle }}>{dup.date}</td>
                      <td style={{ ...tdStyle, fontWeight: 600 }}>{dup.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Close button */}
        <div style={{ display: "flex", justifyContent: "flex-end", paddingTop: "12px" }}>
          <button
            onClick={onClose}
            style={{
              padding: "12px 32px",
              borderRadius: "10px",
              border: "none",
              background: "linear-gradient(135deg, #2e7cba 0%, #2e7cba 100%)",
              color: "#ffffff",
              fontSize: fontBody,
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              cursor: "pointer",
              transition: "all 0.2s",
              minWidth: "120px",
            }}
          >
            {t ? t("close") : "Done"}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes adResultsFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes adResultsSlideUp {
          from {
            opacity: 0;
            transform: translate(-50%, -50%) translateY(30px);
          }
          to {
            opacity: 1;
            transform: translate(-50%, -50%) translateY(0);
          }
        }
      `}</style>
    </>
  );
};

export default AllowanceDeductionImportResultsModal;
